import axios, { AxiosError } from 'axios';

/**
 * Centralized error handling utility for the Al Maysarah IM Admin Dashboard
 * Maps HTTP status codes to user-friendly messages and implements retry logic
 * 
 * Requirements: 6.1, 6.7
 */

export interface AppError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  retryable?: boolean;
  status?: number;
  timestamp: string;
}

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  onRetry?: (attempt: number, error: AppError) => void;
}

/**
 * Error handler utility class
 */
export class ErrorHandler {
  /**
   * Map HTTP status codes to user-friendly error messages
   */
  private static readonly ERROR_MESSAGES: Record<number, string> = {
    400: 'Invalid request. Please check your input and try again.',
    401: 'Your session has expired. Please log in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource could not be found.',
    408: 'Request timeout. Please try again.',
    409: 'This action conflicts with existing data.',
    422: 'Please check your input and correct any errors.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Something went wrong on our end. Please try again.',
    502: 'Service temporarily unavailable. Please try again.',
    503: 'Service temporarily unavailable. Please try again.',
    504: 'Request timeout. Please try again.',
  };

  /**
   * Determine if an error is retryable based on status code
   */
  private static isRetryable(status?: number): boolean {
    if (!status) return true; // Network errors are retryable
    
    // Retry on server errors (5xx) and rate limiting (429)
    return status >= 500 || status === 429 || status === 408;
  }

  /**
   * Handle and normalize errors from various sources
   */
  static handle(error: unknown): AppError {
    const timestamp = new Date().toISOString();

    // Handle Axios errors
    if (axios.isAxiosError(error)) {
      return this.handleAxiosError(error, timestamp);
    }

    // Handle standard JavaScript errors
    if (error instanceof Error) {
      return this.handleStandardError(error, timestamp);
    }

    // Handle unknown errors
    return {
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred. Please try again.',
      retryable: true,
      timestamp,
    };
  }

  /**
   * Handle Axios-specific errors
   */
  private static handleAxiosError(error: AxiosError, timestamp: string): AppError {
    const status = error.response?.status;
    const data = error.response?.data as any;

    // Network error (no response)
    if (!error.response) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect. Please check your internet connection.',
        retryable: true,
        timestamp,
      };
    }

    // Get user-friendly message
    const message = this.ERROR_MESSAGES[status!] || 'An error occurred. Please try again.';

    // Extract additional details from response
    const details: Record<string, unknown> = {};
    if (data?.errors) {
      details.errors = data.errors;
    }
    if (data?.details) {
      details.details = data.details;
    }

    return {
      code: this.getErrorCode(status!, data),
      message: data?.message || data?.error || message,
      details: Object.keys(details).length > 0 ? details : undefined,
      retryable: this.isRetryable(status),
      status,
      timestamp,
    };
  }

  /**
   * Handle standard JavaScript errors
   */
  private static handleStandardError(error: Error, timestamp: string): AppError {
    // Check for specific error types
    if (error.message.toLowerCase().includes('network')) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect. Please check your internet connection.',
        retryable: true,
        timestamp,
      };
    }

    if (error.message.toLowerCase().includes('timeout')) {
      return {
        code: 'TIMEOUT_ERROR',
        message: 'Request timeout. Please try again.',
        retryable: true,
        timestamp,
      };
    }

    return {
      code: 'CLIENT_ERROR',
      message: error.message || 'An error occurred. Please try again.',
      retryable: false,
      timestamp,
    };
  }

  /**
   * Get error code from status and response data
   */
  private static getErrorCode(status: number, data?: any): string {
    if (data?.code) return data.code;

    const codeMap: Record<number, string> = {
      400: 'BAD_REQUEST',
      401: 'UNAUTHORIZED',
      403: 'FORBIDDEN',
      404: 'NOT_FOUND',
      408: 'TIMEOUT',
      409: 'CONFLICT',
      422: 'VALIDATION_ERROR',
      429: 'RATE_LIMITED',
      500: 'SERVER_ERROR',
      502: 'BAD_GATEWAY',
      503: 'SERVICE_UNAVAILABLE',
      504: 'GATEWAY_TIMEOUT',
    };

    return codeMap[status] || 'UNKNOWN_ERROR';
  }

  /**
   * Retry a function with exponential backoff
   * 
   * @param fn - The async function to retry
   * @param options - Retry configuration options
   * @returns Promise resolving to the function result
   * 
   * Requirements: 6.7 - Exponential backoff retry logic
   */
  static async retry<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      onRetry,
    } = options;

    let lastError: AppError | undefined;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = this.handle(error);

        // Don't retry if error is not retryable
        if (!lastError.retryable) {
          throw lastError;
        }

        // Don't retry if we've exhausted attempts
        if (attempt === maxRetries) {
          throw lastError;
        }

        // Calculate delay with exponential backoff: baseDelay * 2^attempt
        const delay = baseDelay * Math.pow(2, attempt);

        // Call retry callback if provided
        if (onRetry) {
          onRetry(attempt + 1, lastError);
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError!;
  }

  /**
   * Format error for display to users
   * Ensures no technical details are exposed
   * 
   * Requirements: 6.1 - User-friendly error messages
   */
  static formatForDisplay(error: AppError): string {
    // Never show technical details in production
    if (import.meta.env.PROD) {
      return error.message;
    }

    // In development, optionally show error code
    return `${error.message}${error.code ? ` (${error.code})` : ''}`;
  }

  /**
   * Log error to console in development or monitoring service in production
   */
  static log(error: AppError, context?: Record<string, unknown>): void {
    const logData = {
      ...error,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    if (import.meta.env.DEV) {
      console.error('[ErrorHandler]', logData);
    } else {
      // In production, send to monitoring service
      // This would integrate with services like Sentry, LogRocket, etc.
      if (window.__errorTracker) {
        window.__errorTracker(logData);
      }
    }
  }

  /**
   * Check if error requires authentication
   */
  static requiresAuth(error: AppError): boolean {
    return error.status === 401 || error.code === 'UNAUTHORIZED';
  }

  /**
   * Check if error is a permission issue
   */
  static isForbidden(error: AppError): boolean {
    return error.status === 403 || error.code === 'FORBIDDEN';
  }

  /**
   * Check if error is a network issue
   */
  static isNetworkError(error: AppError): boolean {
    return error.code === 'NETWORK_ERROR' || !error.status;
  }

  /**
   * Check if error is a validation issue
   */
  static isValidationError(error: AppError): boolean {
    return error.status === 422 || error.code === 'VALIDATION_ERROR';
  }
}

/**
 * Convenience function for handling errors
 */
export const handleError = (error: unknown): AppError => ErrorHandler.handle(error);

/**
 * Convenience function for retrying operations
 */
export const retryOperation = <T>(
  fn: () => Promise<T>,
  options?: RetryOptions
): Promise<T> => ErrorHandler.retry(fn, options);

/**
 * Type augmentation for error tracker
 */
declare global {
  interface Window {
    __errorTracker?: (error: any) => void;
  }
}

export default ErrorHandler;
